import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardActionArea,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { 
  restaurantTableApi, 
  RestaurantTableDto,
  restaurantActiveSaleApi,
  restaurantTableGroupApi,
  RestaurantTableGroupDto
} from '../../../../../services/restaurantApiService';

interface TransferTableModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targetTableId: number, targetTableName: string) => void;
  onRequestMerge: (targetTableId: number, targetTableName: string) => void; // ✅ YENİ: Birleştirme talebi
  currentTableId: number;
  currentTableName: string;
  restaurantScreenId: number;
  isProcessing?: boolean;
}

const TransferTableModal: React.FC<TransferTableModalProps> = ({
  open,
  onClose,
  onConfirm,
  onRequestMerge, // ✅ YENİ
  currentTableId,
  currentTableName,
  restaurantScreenId,
  isProcessing = false,
}) => {
  const [tables, setTables] = useState<RestaurantTableDto[]>([]);
  const [tableGroups, setTableGroups] = useState<RestaurantTableGroupDto[]>([]);
  const [activeTables, setActiveTables] = useState<Set<number>>(new Set()); // ✅ YENİ: Aktif masa ID'leri
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
  const [selectedTable, setSelectedTable] = useState<RestaurantTableDto | null>(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null); // ✅ Mobil dropdown için
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // md altında mobil

  // Masaları yükle
  useEffect(() => {
    if (open && restaurantScreenId) {
      loadTables();
    }
  }, [open, restaurantScreenId]);

  const loadTables = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Tüm masaları, grupları ve aktif satışları paralel olarak yükle
      const [allTables, groups, activeSales] = await Promise.all([
        restaurantTableApi.getByRestaurantScreenId(restaurantScreenId),
        restaurantTableGroupApi.getByRestaurantScreenId(restaurantScreenId),
        restaurantActiveSaleApi.getByRestaurantScreenId(restaurantScreenId)
      ]);
      
      // Aktif masa ID'lerini Set'e ekle (hızlı lookup için)
      const activeTableIds = new Set(activeSales.map(sale => sale.TableId));
      setActiveTables(activeTableIds);
      
      // Masa gruplarını doğal sıralama ile kaydet
      const sortedGroups = groups.sort((a, b) => 
        a.Name.localeCompare(b.Name, undefined, { numeric: true, sensitivity: 'base' })
      );
      setTableGroups(sortedGroups);
      
      // Mevcut masayı hariç tut
      const filteredTables = allTables.filter(t => t.Id !== currentTableId);
      
      // Doğal sıralama (Masa 1, Masa 2, ..., Masa 10)
      const sortedTables = filteredTables.sort((a, b) => 
        a.Name.localeCompare(b.Name, undefined, { numeric: true, sensitivity: 'base' })
      );
      
      setTables(sortedTables);
      
      console.log('🔄 [MASA TAŞI MODAL] Yüklendi:', {
        toplamMasa: sortedTables.length,
        aktifMasa: activeTableIds.size,
        grupSayısı: sortedGroups.length
      });
    } catch (err: any) {
      console.error('Masalar yüklenirken hata:', err);
      setError('Masalar yüklenemedi: ' + (err.response?.data?.Message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Seçili gruba göre filtrelenmiş masalar
  const filteredTables = tables.filter(table => {
    if (selectedGroupId === 'all') return true;
    if (selectedGroupId === 'ungrouped') return !table.TableGroupId;
    return table.TableGroupId?.toString() === selectedGroupId;
  });

  const handleTableSelect = (table: RestaurantTableDto) => {
    setSelectedTable(table);
  };

  const handleConfirm = () => {
    if (selectedTable) {
      // ✅ Eğer hedef masa aktifse → Birleştirme modalını aç
      if (activeTables.has(selectedTable.Id)) {
        onRequestMerge(selectedTable.Id, selectedTable.Name);
      } else {
        // ✅ Boş masaya → Normal aktarma
        onConfirm(selectedTable.Id, selectedTable.Name);
      }
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedTable(null);
      setSelectedGroupId('all');
      setError(null);
      onClose();
    }
  };

  // ✅ Mobil dropdown menü
  const handleOpenGroupMenu = (event: React.MouseEvent<HTMLElement>) => {
    setGroupMenuAnchor(event.currentTarget);
  };

  const handleCloseGroupMenu = () => {
    setGroupMenuAnchor(null);
  };

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
    handleCloseGroupMenu();
  };

  // Seçili grup adını al
  const getSelectedGroupName = () => {
    if (selectedGroupId === 'all') return 'Tüm Masalar';
    if (selectedGroupId === 'ungrouped') return 'Diğer Masalar';
    const group = tableGroups.find(g => g.Id.toString() === selectedGroupId);
    return group?.Name || 'Grup Seç';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={isMobile ? 'sm' : 'lg'}
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle sx={{ px: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon color="primary" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
          <Typography variant="h6" component="span" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
            Masa Aktarma
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: { xs: 'auto', md: 500 } }}>
        {/* Mevcut Masa Bilgisi */}
        <Alert severity="info" sx={{ m: { xs: 1, md: 2 }, mb: 1 }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' } }}>
            <strong>{currentTableName}</strong> aktif satışını hangi masaya taşımak istiyorsunuz?
          </Typography>
        </Alert>

        {error && <Alert severity="error" sx={{ mx: { xs: 1, md: 2 }, mb: 1 }}>{error}</Alert>}

        {/* ✅ MOBİL: Grup Seçim Butonu (sadece mobilde göster) */}
        {isMobile && (
          <Box sx={{ px: 1, pb: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleOpenGroupMenu}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{ justifyContent: 'space-between', textTransform: 'none' }}
            >
              {getSelectedGroupName()}
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', height: { xs: 'auto', md: 'calc(100% - 80px)' } }}>
          {/* Sol: Masa Grupları (sadece desktop'ta göster) */}
          {!isMobile && (
            <Box
              sx={{
                width: 200,
                minWidth: 200,
                bgcolor: '#f5f7fa',
                borderRight: '1px solid #e0e0e0',
                p: 1.5,
                overflowY: 'auto',
              }}
            >
            <ToggleButtonGroup
              orientation="vertical"
              value={selectedGroupId}
              exclusive
              onChange={(e, newValue) => newValue && setSelectedGroupId(newValue)}
              sx={{
                width: '100%',
                flexDirection: 'column',
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  fontSize: '0.85rem',
                  border: '1px solid #d0d7de !important',
                  borderRadius: '6px !important',
                  py: 0.75,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                },
              }}
            >
              <ToggleButton
                value="all"
                sx={{
                  bgcolor: selectedGroupId === 'all' ? 'primary.main' : 'white',
                  color: selectedGroupId === 'all' ? 'white' : 'text.primary',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <AllInclusiveIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                Tümü
              </ToggleButton>
              {tableGroups.map((group) => (
                <ToggleButton
                  key={group.Id}
                  value={group.Id.toString()}
                  sx={{
                    bgcolor: selectedGroupId === group.Id.toString() ? 'primary.main' : 'white',
                    color: selectedGroupId === group.Id.toString() ? 'white' : 'text.primary',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                  }}
                >
                  {group.Name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            </Box>
          )}

          {/* Sağ: Masa Listesi */}
          <Box sx={{ flex: 1, p: { xs: 1, md: 2 }, overflowY: 'auto', maxHeight: { xs: 400, md: 'none' } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : filteredTables.length === 0 ? (
              <Alert severity="warning">
                {selectedGroupId === 'all' ? 'Taşınabilecek masa bulunamadı' : 'Bu grupta masa bulunamadı'}
              </Alert>
            ) : (
              <Grid container spacing={{ xs: 0.75, md: 1 }}>
                {filteredTables.map((table) => {
                  const isActive = activeTables.has(table.Id);
                  const isSelected = selectedTable?.Id === table.Id;
                  
                  return (
                    <Grid item xs={4} sm={3} md={2.4} lg={2} xl={1.5} key={table.Id}>
                      <Card
                        sx={{
                          bgcolor: isSelected ? 'primary.main' : (isActive ? 'success.main' : 'background.paper'),
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.dark' : (isActive ? 'success.dark' : 'divider'),
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: isSelected ? 'primary.dark' : 'primary.light',
                            boxShadow: 3,
                          },
                        }}
                        elevation={isSelected ? 4 : (isActive ? 2 : 1)}
                      >
                      <CardActionArea
                        onClick={() => handleTableSelect(table)}
                        disabled={isProcessing}
                        sx={{ p: { xs: 0.75, md: 1 } }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.2, md: 0.3 } }}>
                          <TableRestaurantIcon 
                            sx={{ 
                              fontSize: { xs: 24, sm: 26, md: 28 }, 
                              color: (isSelected || isActive) ? 'white' : 'primary.main' 
                            }} 
                          />
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ 
                              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                              color: (isSelected || isActive) ? 'white' : 'text.primary',
                              textAlign: 'center',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {table.Name}
                          </Typography>
                          {table.Capacity > 0 && (
                            <Chip
                              label={`${table.Capacity}`}
                              size="small"
                              sx={{
                                height: { xs: 14, md: 16 },
                                fontSize: { xs: '0.55rem', md: '0.6rem' },
                                bgcolor: (isSelected || isActive) ? 'rgba(255,255,255,0.3)' : 'action.hover',
                                color: (isSelected || isActive) ? 'white' : 'text.secondary',
                              }}
                            />
                          )}
                        </Box>
                      </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* ✅ MOBİL: Grup Seçim Dropdown Menüsü */}
      <Menu
        anchorEl={groupMenuAnchor}
        open={Boolean(groupMenuAnchor)}
        onClose={handleCloseGroupMenu}
        PaperProps={{
          sx: { maxHeight: 400, width: '250px' }
        }}
      >
        <MenuItem 
          selected={selectedGroupId === 'all'}
          onClick={() => handleGroupSelect('all')}
        >
          <AllInclusiveIcon sx={{ mr: 1, fontSize: '1rem' }} />
          Tüm Masalar
        </MenuItem>
        {tableGroups.map((group) => (
          <MenuItem
            key={group.Id}
            selected={selectedGroupId === group.Id.toString()}
            onClick={() => handleGroupSelect(group.Id.toString())}
          >
            {group.Name}
          </MenuItem>
        ))}
      </Menu>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, pb: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={handleClose} 
          disabled={isProcessing}
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
        >
          İptal
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!selectedTable || isProcessing}
          variant="contained"
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          startIcon={isProcessing ? <CircularProgress size={20} /> : <SwapHorizIcon />}
        >
          {isProcessing 
            ? (selectedTable && activeTables.has(selectedTable.Id) ? 'Birleştiriliyor...' : 'Aktarılıyor...') 
            : (selectedTable && activeTables.has(selectedTable.Id) ? 'Masa Birleştir' : 'Masayı Aktar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferTableModal;

